import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { gameService } from '@/services/game.service'
import { toStartOfDayISO, toEndOfDayISO } from '@/components/gamificationV2/form/dateHelper'
import { useUsuariosStore } from './usuariosStore'

import type { CompetitionDto, PillarWithDomainEventsDto, ScoreDto } from '@/services/game.service'

export const useGameAdminStore = defineStore('gameAdmin', () => {
  const userStore = useUsuariosStore()

  const pillars = ref<PillarWithDomainEventsDto[]>([])
  const competitionList = ref<CompetitionDto[]>([])

  const users = computed(() => userStore.pagedList.list)
  const pointRequests = ref<{
    id: string
    competitionId: string | null
    userId: string
    domainEventId: string
    eventName: string
    points: number
    entityType: 'Course' | 'Trail' | 'Media' | 'Journey' | null
    entityId: string | null
    missionId: string | null
    missionName: string | null
    attachmentUrl: string | null
    description: string
    status: 'Pending' | 'Approved' | 'Rejected'
    reviewedByUserId: string | null
    reviewNotes: string | null
    reviewedAt: string | null
    active: boolean
    createdAt: string
  }[]>([])
  const pointsAudit = ref<any[]>([])

  const getAll = async () => {
    try {
      const response = await gameService.competitions.getAll({ page: 1, amount: 100 })

      competitionList.value = response.data || []
    } catch (error) {
      console.error(error)
    }
  }

  const getOne = async (id: string) => {
    try {
      await ensurePillarDomains(id)

      const [competition, teams, teamParticipants, events, missions] = await Promise.all([
        gameService.competitions.getById(id),
        gameService.teams.getAll({ competitionId: id }),
        gameService.competitionParticipants.users(id),
        gameService.competitionEvents.getAll({ competitionId: id }),
        gameService.missions.getAll({ competitionId: id })
      ])

      return {
        competition: competition.data,
        teams: teams.data?.map(t => ({
          ...t,
          members: teamParticipants.data?.filter((p: any) => p.teamId === t.id)?.map(p => {
            return {
              participantId: p.participantId,
              userId: p.userId,
              teamId: p.teamId
            }
          }) || []
        })) || [],
        events: events.data || [],
        missions: missions.data || []
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getPillarDomains = async (competitionId: string) => {
    try {
      const response = await gameService.pillars.getWithDomainEvents(competitionId)
      pillars.value = response.data
    } catch (error) {
      console.error(error)
    }
  }

  const removeCompetition = async (id: string, active: boolean) => {
    try {
      // Por algum raio de motivo a competição não pode ser deletada se estiver ativa, ainda que não tenha times, eventos ou missões
      // Pra piorar se chamar close, em uma competição que já está inativa, o endpoint retorna erro... kkk
      if (active) {
        await gameService.competitions.close(id)
      }

      await gameService.competitions.delete(id)
      await getAll()
    } catch (error) {
      console.error(error)
    }
  }

  const ensureCompetitionList = async () => {
    if (competitionList.value.length === 0) {
      await getAll()
    }
  }

  const ensurePillarDomains = async (competitionId?: string) => {
    await getPillarDomains(competitionId || '')
  }

  //
  const addTeamBatch = (competitionId: string, teams: any[]) => {
    if (teams.length === 0) {
      return Promise.resolve()
    }

    const teamsRequest = teams.map((t: any) => ({
      competitionId,
      name: t.name,
      formationType: t.type,
      description: t.description,
      participants: t.participants.map((userId: string) => {
        const user = users.value.find((u: any) => u.id === userId)
        return {
          userId,
          roleId: user?.cargoId,
          departmentId: user?.departamentoId,
          unitId: user?.unidadeDeNegocioId
        }
      })
    }))

    return gameService.teams.postBatch(competitionId, teamsRequest)
  }

  //
  const updateTeamBatch = (competitionId: string, teams: any[], participantsToRemove: { userId: string }[]) => {
    if (teams.length === 0) {
      return Promise.resolve()
    }

    const teamsRequest = teams.map((t: any) => {
      return {
        id: t.id,
        name: t.name,
        formationType: t.type,
        description: t.description,
        participantsToAdd: t.participantsToAdd?.map((userId: string) => {
          const user = users.value.find((u: any) => u.id === userId)
          return {
            userId,
            roleId: user?.cargoId,
            departmentId: user?.departamentoId,
            unitId: user?.unidadeDeNegocioId
          }
        })
      }
    })

    return gameService.teams.patchBatch(competitionId, teamsRequest as any, participantsToRemove)
  }

  //
  const addMissionBatch = (competitionId: string, missions: any[]) => {
    if (missions.length === 0) {
      return Promise.resolve()
    }

    const missionsRequest = missions.map((m: any) => {
      return {
        competitionId,
        name: m.name,
        description: m.description,
        frequency: m.frequency,
        startDate: m.frequency === 'Period' ? toStartOfDayISO(m.startDate) : null,
        endDate: m.frequency === 'Period' ? toEndOfDayISO(m.endDate) : null,
        missionContents: m.missionContents?.filter((mc: any) => !mc.markForDeletion).map((mc: any) => ({
          entityType: mc.entityType,
          entityId: mc.entityId
        })) || []
      }
    })

    return gameService.missions.postBatch(missionsRequest)
  }

  //
  const getPointRequests = async (params: any) => {
    try {
      const response = await gameService.pointsRequest.admin.getAll(params)
      if (response) {
        pointRequests.value = response
      }
    } catch (error) {
      console.error(error)
    }
  }

  //
  const approve = async (id: string) => {
    try {
      const response = await gameService.pointsRequest.approve(id)
      if (response.data?.id) {
        const index = pointRequests.value.findIndex((r) => r.id === response.data?.id)
        if (index !== -1) {
          pointRequests.value[index] = (response.data as any)
        }
      }
      return response
    }
    catch (error) {
      console.error(error)
      throw error
    }
  }

  const getScoreHistory = async (params?: any) => {
    try {
      const response = await gameService.gamificationAudits.getAll({
        ...params,
        ActionType: 'BONUS'
      })

      if (response) {
        pointsAudit.value = response?.data || []
      }
    } catch (error) {
      console.error(error)
    }
  }

  //
  const reject = async (id: string, reviewNotes: string) => {
    try {
      const response = await gameService.pointsRequest.reject(id, { reviewNotes })
      if (response.data?.id) {
        const index = pointRequests.value.findIndex((r) => r.id === response.data?.id)
        if (index !== -1) {
          pointRequests.value[index] = (response.data as any)
        }
      }
      return response
    }
    catch (error) {
      console.error(error)
      throw error
    }
  }

  // OnStart
  userStore.getAll()

  return {
    competitionList,
    getAll,
    getOne,
    pillars,
    getPillarDomains,
    removeCompetition,
    ensureCompetitionList,
    ensurePillarDomains,

    //
    addTeamBatch,
    updateTeamBatch,
    addMissionBatch,

    //
    pointRequests: computed(() => {
      return pointRequests.value?.map((r) => {
        const user = users.value.find((u: any) => u.id === r.userId)
        const reviewer = users.value.find((u: any) => u.id === r.reviewedByUserId)

        return {
          ...r,
          userName: user?.nomeCompleto || '[Usuário não encontrado]',
          userEmail: user?.email || '',
          reviewerName: reviewer?.nomeCompleto || '[Revisor não encontrado]'
        }
      })
    }),
    getPointRequests,
    approveRequest: approve,
    rejectRequest: reject,
    getScoreHistory,
    pointsAudit: computed(() => {
      return pointsAudit.value?.map((r) => {
        const user = users.value.find((u: any) => u.id === r.targetId)
        const reviewer = users.value.find((u: any) => u.id === r.changedByUserId)

        return {
          ...r,
          userName: user?.nomeCompleto || '[Usuário não encontrado]',
          reviewerName: reviewer?.nomeCompleto || '[Revisor não encontrado]'
        }
      })
    })
  }
})